/**
 * SparkModalPortal — behavioral wrapper around the static Spark `Modal`.
 *
 * Adds runtime behaviors Storybook Modal does not provide:
 *   - Portal rendering to document.body
 *   - Fade-in / fade-out for mask + dialog
 *   - Overlay click-to-close
 *   - Escape-key to close (when keyboard=true)
 *   - Body scroll-lock while open
 *   - destroyOnClose — unmounts children after close animation
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type TransitionEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useOverlayEscape } from '../../hooks/useOverlayEscape';
import './SparkModalPortal.css';

export type SparkModalPortalSize = 'text' | 'slot' | 'wide';

/** Runtime modal portal with fade animation, overlay, escape-key close, scroll-lock, and focus trap. */
export type SparkModalPortalProps = {
  open: boolean;
  onClose: () => void;
  afterOpenChange?: (open: boolean) => void;
  mask?: boolean;
  /** When false, clicking the overlay does not close the dialog (close only from inside it). Defaults to true. */
  maskClosable?: boolean;
  keyboard?: boolean;
  destroyOnClose?: boolean;
  /** Trap focus inside the dialog while open. Defaults to true. */
  focusTrap?: boolean;
  /** Dialog width preset. */
  size?: SparkModalPortalSize;
  /** Explicit dialog width (px number or CSS length). Overrides the `size` preset width, still capped at the viewport. */
  width?: number | string;
  /**
   * Vertical placement of the dialog. `'center'` (default) centers it in the
   * viewport, so the top edge moves as content grows. `'top'` anchors the top
   * edge at a constant offset below the navbar, so a dialog whose content
   * grows (e.g. dynamic form rows) keeps a stable position; height is capped
   * to preserve a bottom gap and the body scrolls instead.
   */
  verticalAlign?: 'center' | 'top';
  /** When true, keep mounted but hide visually (preserves child state). */
  hidden?: boolean;
  /**
   * Decorative, full-viewport content layered between the dimmed mask and
   * the dialog (e.g. `<Confetti />`). It fades with the dialog, never
   * receives pointer events, and is unmounted together with the portal.
   */
  backdrop?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

export function SparkModalPortal({
  open,
  onClose,
  afterOpenChange,
  mask = true,
  maskClosable = true,
  keyboard = true,
  destroyOnClose = true,
  focusTrap: enableFocusTrap = true,
  size = 'slot',
  width,
  verticalAlign = 'center',
  hidden = false,
  backdrop,
  children,
  className = '',
  style,
}: SparkModalPortalProps) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;

  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open && !hidden && enableFocusTrap);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        setVisible(true);
      });
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
    return undefined;
  }, [open]);

  const handleTransitionEnd = useCallback(
    (e: TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      const isNowOpen = openRef.current;
      afterOpenChange?.(isNowOpen);
      if (!isNowOpen && destroyOnClose) {
        setMounted(false);
      }
    },
    [afterOpenChange, destroyOnClose],
  );

  useOverlayEscape(open && !hidden, onClose, keyboard);

  useEffect(() => {
    if (!open || hidden) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, hidden]);

  if (!mounted) return null;

  const rootClasses = [
    'spark-modal-portal-root',
    verticalAlign === 'top' ? 'spark-modal-portal-root--top' : '',
    hidden ? 'spark-modal-portal-root--hidden' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const maskClasses = [
    'spark-modal-portal-mask',
    visible && mask && !hidden ? 'spark-modal-portal-mask--visible' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const backdropClasses = [
    'spark-modal-portal-backdrop',
    visible && !hidden ? 'spark-modal-portal-backdrop--visible' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const dialogClasses = [
    'spark-modal-portal-dialog',
    `spark-modal-portal-dialog--${size}`,
    visible && !hidden ? 'spark-modal-portal-dialog--open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div className={rootClasses} style={style}>
      {mask ? (
        <div
          className={maskClasses}
          onClick={hidden || !maskClosable ? undefined : onClose}
          aria-hidden="true"
        />
      ) : null}
      {backdrop ? (
        <div className={backdropClasses} aria-hidden="true">
          {backdrop}
        </div>
      ) : null}
      <div
        ref={dialogRef}
        className={dialogClasses}
        style={width !== undefined ? { width, maxWidth: '100%' } : undefined}
        role="dialog"
        aria-modal="true"
        onTransitionEnd={handleTransitionEnd}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
